import { signInWithAuthentik } from '@/lib/auth-client';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@iq/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Single-button SSO sign-in. Initiates the Authentik OIDC flow and
 * redirects the browser to the provider authorization URL. Rendered by the
 * sign-in route in place of the email/password form when SSO is enabled.
 */
export function AuthentikButton({ label }: { label?: string }) {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onContinue() {
    setError(null);
    setPending(true);
    const result = await signInWithAuthentik();
    if (result.ok) {
      // Full-page navigation to the provider; do not reset `pending` so the
      // button stays disabled until the browser leaves the page.
      window.location.href = result.url;
      return;
    }
    setPending(false);
    setError(result.message);
  }

  return (
    <Card className="w-full max-w-md" data-testid="authentik-signin-card">
      <CardHeader>
        <CardTitle>{t('auth.signIn.title')}</CardTitle>
        <CardDescription>{t('auth.signIn.ssoSubtitle')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {error ? (
          <p role="alert" className="text-sm text-destructive" data-testid="authentik-error">
            {error}
          </p>
        ) : null}
        <Button
          type="button"
          onClick={onContinue}
          disabled={pending}
          className="w-full"
          data-testid="authentik-signin"
        >
          {pending
            ? t('auth.signIn.redirecting')
            : (label ?? t('auth.signIn.continueWithAuthentik'))}
        </Button>
      </CardContent>
    </Card>
  );
}
